import type {
  ClassroomEntry,
  ClassroomView,
  FamilySession,
  LessonAvailability,
  LessonListItem,
  MasteryEvidence,
  PublicLesson,
  RetentionTask,
  SessionSnapshot,
} from "./contracts";
import { demoLessons, findDemoLesson } from "./demo-lessons";

const STORAGE_KEY = "mathos-v2-demo-state-r4";

export const HOUSEHOLD_ID = "f59db2ef-b770-4ada-b679-f5673a6c7a01";
export const CHILD_ID = "83130572-9ca8-42be-b1e2-ebdfb2574c45";
export const RETENTION_ID = "50337e86-3ea7-489d-9de4-a970fed86fb4";

export interface DemoSessionRecord {
  snapshot: SessionSnapshot;
  entries: ClassroomEntry[];
  evidence: MasteryEvidence[];
  answerReceipt?: {
    idempotencyKey: string;
    savedAt: string;
  };
  pauseCause?: "teacher_unavailable" | "manually_paused";
}

export interface DemoState {
  family: FamilySession | null;
  childBirthYear?: number;
  sessions: Record<string, DemoSessionRecord>;
  retentionEvidence?: MasteryEvidence;
}

const memoryState: DemoState = {
  family: null,
  sessions: {},
};

export function readDemoState(): DemoState {
  if (typeof window === "undefined") return memoryState;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return memoryState;
  try {
    return JSON.parse(raw) as DemoState;
  } catch {
    return memoryState;
  }
}

export function writeDemoState(state: DemoState): void {
  if (typeof window === "undefined") {
    memoryState.family = state.family;
    if (state.childBirthYear === undefined) {
      delete memoryState.childBirthYear;
    } else {
      memoryState.childBirthYear = state.childBirthYear;
    }
    memoryState.sessions = state.sessions;
    if (state.retentionEvidence) {
      memoryState.retentionEvidence = state.retentionEvidence;
    } else {
      delete memoryState.retentionEvidence;
    }
    return;
  }
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function demoDelay<T>(value: T, milliseconds = 180): Promise<T> {
  return new Promise((resolve) => {
    globalThis.setTimeout(() => resolve(value), milliseconds);
  });
}

export function requireDemoFamily(): FamilySession {
  const family = readDemoState().family;
  if (!family) throw new Error("请先通过家庭邀请进入。");
  return family;
}

export function availabilityFor(
  lessonId: string,
  sessions: Record<string, DemoSessionRecord>,
): LessonAvailability {
  const session = Object.values(sessions).find(
    (candidate) => candidate.snapshot.lessonId === lessonId,
  );
  if (session?.snapshot.status === "completed") return "completed";
  if (session) return "in_progress";
  if (lessonId === "L24") return "completed";
  if (lessonId === "L30") return "retention_due";
  return "ready";
}

export function toLessonListItem(
  lessonId: string,
  sessions: Record<string, DemoSessionRecord>,
): LessonListItem {
  const lesson = findDemoLesson(lessonId);
  const availability = availabilityFor(lessonId, sessions);
  const session = Object.values(sessions).find(
    (candidate) => candidate.snapshot.lessonId === lessonId,
  );
  const phaseIndex = session?.snapshot.activityIndex ?? 0;
  const sequenceNumber = demoLessons.findIndex(
    (candidate) => candidate.id === lesson.id,
  ) + 1;
  return {
    id: lesson.id,
    familyId: lesson.familyId,
    title: lesson.title,
    topic: lesson.topic,
    estimatedMinutes: lesson.estimatedMinutes,
    availability,
    catalogPosition: {
      sequenceNumber,
      totalLessons: demoLessons.length,
      textbookVolume: null,
    },
    progressPercent:
      availability === "completed"
        ? 100
        : availability === "in_progress"
          ? Math.max(
              18,
              Math.round((phaseIndex / lesson.activities.length) * 100),
            )
          : 0,
    ...(session
      ? { lastStudiedAt: session.snapshot.updatedAt }
      : availability === "completed"
        ? { lastStudiedAt: "2026-07-18T19:20:00+08:00" }
        : {}),
    ...(availability === "completed"
      ? { evidenceKind: "independent_transfer" as const }
      : {}),
  };
}

export function classroomFromRecord(
  record: DemoSessionRecord,
): ClassroomView {
  const lesson = findDemoLesson(record.snapshot.lessonId);
  const currentActivity =
    lesson.activities[record.snapshot.activityIndex] ??
    lesson.activities.at(-1)!;
  return {
    snapshot: record.snapshot,
    lesson: publicDemoLesson(lesson.id),
    currentActivity: {
      id: currentActivity.id,
      phase: currentActivity.phase,
      title: currentActivity.title,
      ...(currentActivity.studentPrompt === undefined
        ? {}
        : { studentPrompt: currentActivity.studentPrompt }),
      independence: currentActivity.independence,
      hints: [...currentActivity.hints],
      ...(currentActivity.verifier === undefined
        ? {}
        : { answerKind: currentActivity.verifier.kind }),
    },
    visualBoard: {
      status: "none",
      materials: [],
    },
    entries: record.entries,
    evidence: record.evidence,
    canRequestHint:
      currentActivity.independence !== "independent" &&
      record.snapshot.status === "active",
    providerPaused: record.snapshot.status === "paused",
    ...(record.snapshot.status === "paused"
      ? { pauseCause: record.pauseCause ?? "manually_paused" }
      : {}),
    ...(record.answerReceipt ? { answerReceipt: record.answerReceipt } : {}),
  };
}

export function publicDemoLesson(lessonId: string): PublicLesson {
  const lesson = findDemoLesson(lessonId);
  return {
    id: lesson.id,
    familyId: lesson.familyId,
    title: lesson.title,
    topic: lesson.topic,
    estimatedMinutes: lesson.estimatedMinutes,
    approvalStatus: lesson.approvalStatus,
    activities: lesson.activities.map((activity) => ({
      id: activity.id,
      phase: activity.phase,
      title: activity.title,
      ...(activity.studentPrompt === undefined
        ? {}
        : { studentPrompt: activity.studentPrompt }),
      independence: activity.independence,
      hints: [...activity.hints],
      ...(activity.verifier === undefined
        ? {}
        : { answerKind: activity.verifier.kind }),
    })),
    sourceStatus: {
      kind: "demo",
      label: "界面演示依据",
      title: "演示占位，未导入教材",
      detail: "未完成授权与 canonical 校订，不显示真实教材页码",
    },
  };
}

export function sampleEvidence(
  lessonId = "L24",
  kind: MasteryEvidence["kind"] = "independent_transfer",
): MasteryEvidence {
  return {
    evidenceId: crypto.randomUUID(),
    sessionId: "d3b142aa-2a2e-43fa-893d-793d7d46a8b1",
    childId: CHILD_ID,
    lessonId,
    activityId: `${lessonId}-transfer`,
    attemptNumber: 1,
    kind,
    studentAnswer: {
      idempotencyKey: crypto.randomUUID(),
      activityId: `${lessonId}-transfer`,
      finalAnswer: {
        kind: "expression",
        latex: "S_4=8+16+32+64=120",
      },
      confidence: "sure",
      submittedAt: "2026-07-18T19:18:00+08:00",
    },
    verification: {
      outcome: "correct",
      normalizedAnswer: "S_4=120",
      reasons: ["公比与项数识别正确，求和过程完整。"],
      matchedClaims: ["公比", "四项和"],
      missingClaims: [],
      forbiddenClaims: [],
      mayAdvance: true,
      needsTeacherClarification: false,
      verifier: "expression-verifier",
    },
    teacherHelpAfterPrompt: false,
    recordedAt: "2026-07-18T19:19:00+08:00",
  };
}

export function demoRetentionTask(
  status: RetentionTask["status"],
): RetentionTask {
  return {
    taskId: RETENTION_ID,
    childId: CHILD_ID,
    lessonId: "L24",
    sourceActivityId: "L24-transfer",
    dueAt: "2026-07-20T19:00:00+08:00",
    status,
    ...(status === "completed"
      ? { completedEvidenceId: readDemoState().retentionEvidence?.evidenceId }
      : {}),
  };
}
